import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, Phone, Send, MapPin, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// WhatsApp Icon
const WhatsAppIcon = ({ size = 24 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Contact = () => {
  const { toast } = useToast();
  const location = useLocation();
  const preselectedService = location.state?.preselectedService || '';
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: preselectedService,
    message: ''
  });

  useEffect(() => {
    if (preselectedService) {
      setFormData(prev => ({ ...prev, service: preselectedService }));
    }
  }, [preselectedService]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        message: formData.message
      };
      // Only include service if user selected one
      if (formData.service && formData.service !== '') {
        payload.service = formData.service;
      }
      await axios.post(`${API}/contact`, payload);
      toast({
        title: "Message Sent!",
        description: "We'll get back to you as soon as possible.",
      });
      setFormData({ name: '', email: '', service: '', message: '' });
    } catch (error) {
      console.error('Contact error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-black text-white pt-24">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Get in <span className="text-[#F4B400]">Touch</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Let's discuss your project and how we can help bring your vision to life
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-white text-black">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold mb-8">Contact Information</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-[#F4B400] p-3 rounded-lg mr-4">
                    <Phone className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Phone</h3>
                    <a href="tel:+18608470649" className="text-gray-600 hover:text-[#F4B400] transition-colors" data-testid="contact-phone">
                      (860) 847-0649
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-green-500 p-3 rounded-lg mr-4">
                    <WhatsAppIcon className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">WhatsApp Business</h3>
                    <a 
                      href="https://wa.me/18608470649" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-green-500 transition-colors"
                    >
                      Chat with us on WhatsApp
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#F4B400] p-3 rounded-lg mr-4">
                    <Mail className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Email</h3>
                    <a href="mailto:enrigo@ealagon.com" className="text-gray-600 hover:text-[#F4B400] transition-colors" data-testid="contact-email">
                      enrigo@ealagon.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#F4B400] p-3 rounded-lg mr-4">
                    <MapPin className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Office Address</h3>
                    <p className="text-gray-600" data-testid="contact-address">
                      30 Airport Rd<br />
                      Hartford, CT 06114
                    </p>
                    <a 
                      href="https://maps.google.com/?q=30+Airport+Rd,+Hartford+CT+06114" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#F4B400] hover:underline text-sm mt-1 inline-block"
                    >
                      Get Directions
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-12">
                <h3 className="text-2xl font-bold mb-4">Office Hours</h3>
                <p className="text-gray-600">
                  Monday - Friday: 9:00 AM - 6:00 PM<br />
                  Saturday: 10:00 AM - 2:00 PM<br />
                  Sunday: Closed
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold mb-8">Send a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-2"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="mt-2"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <Label htmlFor="service">Service Interested In</Label>
                  <Select value={formData.service || undefined} onValueChange={(value) => setFormData({ ...formData, service: value })}>
                    <SelectTrigger className="mt-2" data-testid="contact-service-select">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="print">Print Design</SelectItem>
                      <SelectItem value="wrap-car">Wrap Car Design</SelectItem>
                      <SelectItem value="wall-wrap">Wall Wrap Design</SelectItem>
                      <SelectItem value="web">Web Design</SelectItem>
                      <SelectItem value="audio-video">Audio & Video</SelectItem>
                      <SelectItem value="events">Events</SelectItem>
                      <SelectItem value="consulting">Corporate Consulting</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={6}
                    className="mt-2"
                    placeholder="Tell us about your project..."
                  />
                </div>
                <Button 
                  type="submit"
                  size="lg"
                  disabled={submitting}
                  className="w-full bg-[#F4B400] text-black hover:bg-[#d49f00] transition-all duration-300 disabled:opacity-70"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={18} />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2" size={18} />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
